import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateTeamMutation, useGetMembersQuery, useUpdateTeamMutation } from "@/store/rest_api";
import { MembershipStatus } from "@/enum/MembershipStatus";
import Team from "@/model/interface/Team";
import Membership from "@/model/interface/Membership";
import { apiErrorMessage } from "@/util/apiError";
import { memberName } from "@/util/member";

interface TeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team: Team | null;
}

function memberLabel(membership: Membership | undefined): string {
  return memberName(membership, membership?.user?.email || "Member");
}

export default function TeamDialog({ open, onOpenChange, team }: TeamDialogProps) {
  const { data: members = [] } = useGetMembersQuery({ status: MembershipStatus.ACTIVE }, { skip: !open });
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation();
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation();
  const [name, setName] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);

  const membersById = new Map(members.map((member) => [String(member.id), member]));
  const addableMembers = members.filter((member) => !memberIds.includes(String(member.id)));
  const isSaving = isCreating || isUpdating;

  const moveMember = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= memberIds.length) {
      return;
    }
    const reordered = [...memberIds];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setMemberIds(reordered);
  };

  const removeMember = (membershipId: string) => {
    setMemberIds(memberIds.filter((candidate) => candidate !== membershipId));
  };

  const save = async () => {
    const data = {
      name: name.trim(),
      members: memberIds.map((membershipId) => ({ membership: { id: membershipId } })),
    };
    if (!data.name) {
      toast.error("Give the team a name");
      return;
    }
    try {
      if (team) {
        await updateTeam({ id: team.id, data }).unwrap();
      } else {
        await createTeam(data).unwrap();
      }
      toast.success(team ? "Team saved" : "Team created");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the team"));
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setName(team?.name ?? "");
    setMemberIds(
      [...(team?.members ?? [])]
        .sort((first, second) => first.position - second.position)
        .map((member) => String(member.membership.id)),
    );
  }, [open, team]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{team ? "Edit team" : "New team"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Name</Label>
            <Input
              id="team-name"
              placeholder="Acquisitions"
              value={name}
              onChange={(changeEvent) => setName(changeEvent.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Members</Label>
            <p className="text-xs text-muted-foreground">
              The order here is the ring order for round robin and one at a time rings.
            </p>
            {memberIds.length === 0 && (
              <p className="text-sm text-muted-foreground">No members yet.</p>
            )}
            <div className="space-y-1">
              {memberIds.map((membershipId, index) => (
                <div key={membershipId} className="flex items-center gap-2 rounded-lg border px-3 py-1.5">
                  <span className="w-5 text-xs font-semibold tabular-nums text-muted-foreground">{index + 1}</span>
                  <span className="flex-1 text-sm font-semibold">{memberLabel(membersById.get(membershipId))}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Move up"
                    title="Move up"
                    disabled={index === 0}
                    onClick={() => moveMember(index, -1)}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Move down"
                    title="Move down"
                    disabled={index === memberIds.length - 1}
                    onClick={() => moveMember(index, 1)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Remove from team"
                    title="Remove from team"
                    onClick={() => removeMember(membershipId)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            {addableMembers.length > 0 && (
              <Select value="" onValueChange={(membershipId) => setMemberIds([...memberIds, membershipId])}>
                <SelectTrigger>
                  <SelectValue placeholder="Add a member" />
                </SelectTrigger>
                <SelectContent>
                  {addableMembers.map((member) => (
                    <SelectItem key={member.id} value={String(member.id)}>
                      {memberLabel(member)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={save} disabled={isSaving}>
            {team ? "Save" : "Create team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
